import apiClient from "@/api/client";
import type { AppliedPenalties, AvailableLineItemType, ContractorPreviewMeta, ContractorTodoData, ContractorTodoPreviewMeta, Order, OrderAccessGrant, OrderAccessGrantsResponse, OrderAccessType, OrderMilestone, OrderProjectFilesResponse, OverduePenaltyRow, PenaltyCalc } from "../types";
import { saveBlobResponse } from "@/utils/download";

export const ordersApi = {
  addMilestone: (orderId: string, payload: { label: string; due_date: string }) =>
    apiClient
      .post<{ data: OrderMilestone }>(`/orders/${orderId}/milestones/`, payload)
      .then((r) => r.data.data),

  updateMilestone: (
    orderId: string,
    milestoneId: string,
    payload: Partial<Pick<OrderMilestone, "label" | "due_date" | "completed_at" | "sequence">>,
  ) =>
    apiClient
      .patch<{ data: OrderMilestone }>(`/orders/${orderId}/milestones/${milestoneId}/`, payload)
      .then((r) => r.data.data),

  deleteMilestone: (orderId: string, milestoneId: string) =>
    apiClient.delete(`/orders/${orderId}/milestones/${milestoneId}/`).then((r) => r.data),

  moveMilestone: (orderId: string, milestoneId: string, direction: "up" | "down") =>
    apiClient
      .post<{ data: OrderMilestone[] }>(`/orders/${orderId}/milestones/${milestoneId}/move/`, { direction })
      .then((r) => r.data.data),

  // „Oznacz jako ukończony" z „Moich terminów" podwykonawcy — bez body; jedyna zmiana
  // kamienia dostępna firmie (nazwa/termin/cofnięcie to PATCH GK wyżej, dla niej 403).
  completeMilestoneAsContractor: (orderId: string, milestoneId: string) =>
    apiClient
      .post<{ data: OrderMilestone }>(`/orders/${orderId}/milestones/${milestoneId}/complete/`)
      .then((r) => r.data.data),

  list: (params?: Record<string, string>) =>
    apiClient.get<{ data: Order[] }>("/orders/", { params }).then((r) => r.data.data),

  // ── Dostęp pracowników GK do zamówienia (OrderAccessGrant) ──────────────────
  // Nadaje zamawiający albo dyrektor techniczny / prezes (backend: can_grant_order_access).
  // `candidates` = pracownicy, którzy zamówienia jeszcze nie widzą (bez ról widzących
  // wszystko: prezes, dyrektorzy, księgowość, kadry; bez koordynatorów przedsięwzięcia).
  accessGrants: (orderId: string) =>
    apiClient
      .get<{ data: OrderAccessGrantsResponse }>(`/orders/${orderId}/access-grants/`)
      .then((r) => r.data.data),

  grantAccess: (orderId: string, payload: { user: string; access_type: OrderAccessType }) =>
    apiClient
      .post<{ data: OrderAccessGrant }>(`/orders/${orderId}/access-grants/`, payload)
      .then((r) => r.data.data),

  updateAccessGrant: (orderId: string, grantId: string, payload: { access_type: OrderAccessType }) =>
    apiClient
      .patch<{ data: OrderAccessGrant }>(`/orders/${orderId}/access-grants/${grantId}/`, payload)
      .then((r) => r.data.data),

  revokeAccessGrant: (orderId: string, grantId: string) =>
    apiClient.delete(`/orders/${orderId}/access-grants/${grantId}/`).then(() => undefined),

  get: (id: string) =>
    apiClient.get<{ data: Order }>(`/orders/${id}/`).then((r) => r.data.data),

  accept: (id: string) =>
    apiClient.patch<{ data: Order }>(`/orders/${id}/accept/`).then((r) => r.data.data),

  // Director/CEO override: accept without the contractor's confirmation (and without logs).
  forceAccept: (id: string) =>
    apiClient.patch<{ data: Order }>(`/orders/${id}/force-accept/`).then((r) => r.data.data),

  // Director/CEO: manually close a finished order settled outside the per-POCK flow
  // (e.g. by a collective invoice). Only from status "accepted_final"; note is mandatory.
  close: (id: string, payload: { settlement_note: string }) =>
    apiClient.patch<{ data: Order }>(`/orders/${id}/close/`, payload).then((r) => r.data.data),

  // Director/CEO: put a FINISHED order back to work — the final POCK went out too early
  // (scope grew, or acceptance was issued by mistake). Only from "accepted_final"; lands in
  // "W realizacji", or "W realizacji (częściowo odebrane)" when a POCK was already accepted.
  // Issued protocols stay untouched — a wrong one has to be withdrawn separately.
  reopen: (id: string, payload: { reason: string }) =>
    apiClient.patch<{ data: Order }>(`/orders/${id}/reopen/`, payload).then((r) => r.data.data),

  // Director/CEO: retire an order that turned out to be pointless — issued by mistake, or
  // its scope evaporated before anyone did anything. Lands in "Anulowane" WITHOUT a penalty
  // (that is `terminate`) and without counting as delivered (that is `close`), so it never
  // reaches the contractor's rating queue nor lights a DLC_01 milestone. Backend refuses
  // once a final POCK was accepted or an invoice exists; reason is mandatory.
  annul: (id: string, payload: { reason: string }) =>
    apiClient.patch<{ data: Order }>(`/orders/${id}/annul/`, payload).then((r) => r.data.data),

  // Technical director (and above): restore a contractor-rejected order back to "Wysłane"
  // (awaiting the contractor's confirmation again).
  restore: (id: string) =>
    apiClient.patch<{ data: Order }>(`/orders/${id}/restore/`).then((r) => r.data.data),

  // Zamawiający (koordynator, dyr. techniczny, CEO, grant „Zarządzanie”): przesuń termin
  // przyjęcia zamówienia „Wysłane” na późniejszy dzień (najdalej 90 dni od dziś). Bez tego
  // nieprzyjęte zamówienie system anuluje sam następnego dnia po terminie.
  extendAcceptanceDeadline: (id: string, payload: { confirmation_deadline: string; reason?: string }) =>
    apiClient
      .post<{ data: Order }>(`/orders/${id}/extend-acceptance-deadline/`, payload)
      .then((r) => r.data.data),

  reject: (id: string, comment: string) =>
    apiClient.patch<{ data: Order }>(`/orders/${id}/reject/`, { comment }).then((r) => r.data.data),

  acknowledge: (id: string) =>
    apiClient.patch<{ data: Order }>(`/orders/${id}/acknowledge/`).then((r) => r.data.data),

  // Ręczne (prze)budowanie wykazu właścicieli — zamówienia WPW sprzed automatu (bez
  // pliku). Ścieżka endpointu zostaje historyczna (kontrakt backendu).
  generateOwnersList: (id: string) =>
    apiClient
      .post<{ data: { task_id: string } }>(`/orders/${id}/generate-mml/`)
      .then((r) => r.data.data),

  pendingCount: () =>
    apiClient.get<{ data: { count: number } }>("/orders/pending_count/").then((r) => r.data.data.count),

  pendingChangeRequestsCount: () =>
    apiClient.get<{ data: { count: number } }>("/orders/pending_change_requests_count/").then((r) => r.data.data.count),

  pendingExternalCostsCount: () =>
    apiClient.get<{ data: { count: number } }>("/orders/external-costs/pending-count/").then((r) => r.data.data.count),

  penaltyCalc: (id: string) =>
    apiClient.get<{ data: PenaltyCalc }>(`/orders/${id}/penalty-calc/`).then((r) => r.data.data),

  terminate: (id: string, payload: {
    amount: string; note?: string; agreement_id?: string;
    penalty_term_id?: string; delay_days?: number;
  }) =>
    apiClient.post<{ data: Order }>(`/orders/${id}/terminate/`, payload).then((r) => r.data.data),

  overduePenalties: () =>
    apiClient.get<{ data: OverduePenaltyRow[] }>("/orders/overdue-penalties/").then((r) => r.data.data),

  forgiveDaily: (id: string, note: string) =>
    apiClient.post<{ data: { daily_penalty_forgiven: boolean; note: string } }>(
      `/orders/${id}/forgive-daily-penalty/`, { note }
    ).then((r) => r.data.data),

  settlePenalties: (id: string, settlement_note: string, amount?: string) =>
    apiClient.post<{ data: { settled_amount: string; settlement_note: string; project_id: string } }>(
      `/orders/${id}/settle-penalties/`, { settlement_note, ...(amount ? { amount } : {}) }
    ).then((r) => r.data.data),

  appliedPenalties: (id: string) =>
    apiClient.get<{ data: AppliedPenalties }>(`/orders/${id}/applied-penalties/`).then((r) => r.data.data),

  availableLineItemTypes: (orderId: string) =>
    apiClient.get<{ data: AvailableLineItemType[] }>(`/orders/${orderId}/available-line-item-types/`).then((r) => r.data.data),

  ceoUpdate: (
    id: string,
    payload: {
      description?: string;
      deadline?: string | null;
      priority?: string;
      contractor?: string;
      ordered_by?: string | null;
      project?: string;
      line_item_result_files?: { id: string; result_files_expected: boolean }[];
    },
  ) =>
    apiClient.patch<{ data: Order }>(`/orders/${id}/ceo-update/`, payload).then((r) => r.data.data),

  // Pole „Pliki pozycji realizacyjnej” per pozycja dla zamawiającego (osoba odpowiedzialna,
  // koordynator przedsięwzięcia, grant „Zarządzanie”). Wyłączenie pozycji z plikami
  // przesłanymi przez podwykonawcę backend odrzuca (400 z errors.detail).
  setLineItemResultFiles: (id: string, entries: { id: string; result_files_expected: boolean }[]) =>
    apiClient
      .patch<{ data: Order }>(`/orders/${id}/line-item-result-files/`, {
        line_item_result_files: entries,
      })
      .then((r) => r.data.data),

  // Attachment management — same permission as the standard order edit (CEO).
  uploadAttachments: (id: string, files: File[]) => {
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    return apiClient
      .post<{ data: Order }>(`/orders/${id}/attachments/`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r.data.data);
  },

  deleteAttachment: (id: string, fileId: string) =>
    apiClient.delete(`/orders/${id}/attachments/${fileId}/`),

  // Działki wskazanych zamówień jako XLSX (identyfikator, adres, nr zamówienia) —
  // autoryzowany widok Django, więc blob przez klienta API; ID-ki idą POST-em,
  // bo plik ma pokrywać dokładnie odfiltrowane po stronie klienta wiersze.
  downloadParcelsXlsx: async (orderIds: string[]) => {
    const resp = await apiClient.post("/orders/parcels-xlsx/", { order_ids: orderIds }, { responseType: "blob" });
    saveBlobResponse(resp, "Dzialki_zamowien.xlsx");
  },

  // „Pobierz zamówienia (.xlsx)" z listy — te same parametry co /orders/feed/ (filtry,
  // ?q=, ?ordering=), ale serwer zwraca WSZYSTKIE pasujące wiersze, nie doczytaną porcję.
  downloadListXlsx: async (params: Record<string, string>) => {
    const resp = await apiClient.get("/orders/export-xlsx/", { params, responseType: "blob" });
    saveBlobResponse(resp, "Zamowienia.xlsx");
  },

  // Wszystkie załączniki zamówienia jednym ZIP-em — autoryzowany widok Django, więc
  // pobieramy blob przez klienta API i zapisujemy po stronie klienta.
  downloadAttachmentsZip: async (id: string) => {
    const resp = await apiClient.get(`/orders/${id}/attachments/zip/`, { responseType: "blob" });
    saveBlobResponse(resp, "Zalaczniki.zip");
  },

  // Pliki z projektu ZE udostępnione w zamówieniu (żywa projekcja zakładki „Pliki";
  // tylko udostępnione, niepoufne foldery). Puste `folders` gdy nic nie udostępniono.
  projectFiles: (id: string) =>
    apiClient
      .get<{ data: OrderProjectFilesResponse }>(`/orders/${id}/project-files/`)
      .then((r) => r.data.data),

  // ZIP udostępnionych plików projektu ZE (całość albo jeden folder) — autoryzowany
  // widok Django, więc pobieramy blob przez klienta API i zapisujemy po stronie klienta.
  downloadProjectFilesZip: async (id: string, folderSlug?: string) => {
    const resp = await apiClient.get(`/orders/${id}/project-files/zip/`, {
      params: folderSlug ? { folder: folderSlug } : undefined,
      responseType: "blob",
    });
    saveBlobResponse(resp, "Pliki_ZE.zip");
  },

  softDelete: (id: string) =>
    apiClient.post(`/orders/${id}/soft-delete/`),

  // Powiadomienia o zamówieniu dla zalogowanej osoby (tylko GK). POST zapisuje,
  // DELETE wypisuje — obie strony idempotentne, więc podwójne kliknięcie nie boli.
  setSubscription: (id: string, enabled: boolean) =>
    (enabled
      ? apiClient.post<{ data: { is_subscribed: boolean } }>(`/orders/${id}/subscribe/`)
      : apiClient.delete<{ data: { is_subscribed: boolean } }>(`/orders/${id}/subscribe/`)
    ).then((r) => r.data.data),

  // „Widok podwykonawcy" (tylko GK): zamówienie zserializowane dokładnie tak, jak widzi
  // je przypisany podwykonawca — ukryte przedsięwzięcia, stawki wg grantów kont firmy.
  // `rates` wymusza wariant widoczności stawek niezależnie od grantów.
  contractorPreview: (id: string, rates?: "visible" | "hidden") =>
    apiClient
      .get<{ data: Order; meta: { contractor_preview: ContractorPreviewMeta } }>(
        `/orders/${id}/contractor-preview/`,
        { params: rates ? { rates } : undefined },
      )
      .then((r) => ({ order: r.data.data, meta: r.data.meta.contractor_preview })),

  // „Moje terminy" podwykonawcy — kamienie + terminy końcowe wszystkich aktywnych
  // zamówień firmy ZALOGOWANEGO konta. Firma zawsze z konta, nigdy z parametru.
  contractorTodo: () =>
    apiClient
      .get<{ data: ContractorTodoData }>(`/orders/contractor-todo/`)
      .then((r) => r.data.data),

  // Podgląd „Moich terminów" wskazanej firmy (tylko dyr. techniczny / CEO) —
  // dokładnie ten sam payload, który widzi firma; nazwa firmy wraca w meta.
  contractorTodoPreview: (contractorId: string) =>
    apiClient
      .get<{ data: ContractorTodoData; meta: { contractor_todo_preview: ContractorTodoPreviewMeta } }>(
        `/orders/contractor-todo-preview/`,
        { params: { contractor: contractorId } },
      )
      .then((r) => ({ data: r.data.data, meta: r.data.meta.contractor_todo_preview })),
};
